import type {TFunction} from "i18next";
import {buildEventWorkspaceNavigation} from "../Pages/Admin/EventWorkspaceLayout";
import {eventWorkspaceRoute} from "../Pages/Admin/eventWorkspaceRoutes";
import {shellBreadcrumbKeys, type ShellBreadcrumbLabels} from "../Contexts/ShellBreadcrumbContext";

export type ShellBreadcrumb = {
    key: string;
    title: string;
    // Omitted for the current page, which is not a link.
    to?: string;
};

const LOADING_LABEL = "…";

const eventSectionTitle = (eventId: number, section: string, t: TFunction) => {
    if (section === "check-in") {
        return t("check-in.station-title", {defaultValue: "Check-in station"});
    }

    const item = buildEventWorkspaceNavigation(eventId)
        .flatMap(group => group.items)
        .find(candidate => candidate.key === section);

    return item ? t(item.labelKey, {defaultValue: item.defaultLabel}) : undefined;
};

const lastWithoutLink = (crumbs: ShellBreadcrumb[]): ShellBreadcrumb[] =>
    crumbs.map((crumb, index) => index === crumbs.length - 1 ? {key: crumb.key, title: crumb.title} : crumb);

// Derives the organizer breadcrumb (Events / event / section, Payers / payer, Hotels / hotel) from the route.
export const buildShellBreadcrumbs = (
    pathname: string,
    search: string,
    labels: ShellBreadcrumbLabels,
    t: TFunction
): ShellBreadcrumb[] => {
    const segments = pathname.replace(/^\/Admin\/?/, "").split("/").filter(Boolean);
    const [area, id, section, childId] = segments;
    const events: ShellBreadcrumb = {key: "events", title: t("events", {defaultValue: "Events"}), to: "/Admin/Events"};

    if (area === "Events") {
        if (id === "Archive" || (!id && new URLSearchParams(search).get("view") === "archived")) {
            return lastWithoutLink([events, {key: "archive", title: t("archive", {defaultValue: "Archive"})}]);
        }

        const eventId = Number(id);
        if (!Number.isInteger(eventId) || eventId <= 0) {
            return lastWithoutLink([events]);
        }

        const crumbs: ShellBreadcrumb[] = [events, {
            key: "event",
            title: labels[shellBreadcrumbKeys.event(eventId)] ?? LOADING_LABEL,
            to: eventWorkspaceRoute(eventId, "overview")
        }];
        const sectionTitle = section ? eventSectionTitle(eventId, section, t) : undefined;

        if (sectionTitle) {
            crumbs.push({key: "section", title: sectionTitle, to: eventWorkspaceRoute(eventId, section)});
        }

        if (section === "participants" && childId) {
            crumbs.push({
                key: "participant",
                title: labels[shellBreadcrumbKeys.participant(childId)]
                    ?? t("participant-detail", {defaultValue: "Participant detail"})
            });
        }

        return lastWithoutLink(crumbs);
    }

    if (area === "Payers") {
        const payers: ShellBreadcrumb = {key: "payers", title: t("payers", {defaultValue: "Payers"}), to: "/Admin/Payers"};
        if (!id) {
            return lastWithoutLink([payers]);
        }

        return lastWithoutLink([payers, {
            key: "payer",
            title: id.toLowerCase() === "new"
                ? t("new-payer", {defaultValue: "New payer"})
                : labels[shellBreadcrumbKeys.payer(id)] ?? LOADING_LABEL
        }]);
    }

    if (area === "Hotels") {
        const hotels: ShellBreadcrumb = {key: "hotels", title: t("hotels", {defaultValue: "Hotels"}), to: "/Admin/Hotels"};
        if (!id) {
            return lastWithoutLink([hotels]);
        }

        return lastWithoutLink([hotels, {
            key: "hotel",
            title: id === "new"
                ? t("hotels-new", {defaultValue: "New hotel"})
                : labels[shellBreadcrumbKeys.hotel(id)] ?? LOADING_LABEL
        }]);
    }

    if (area === "Profile") {
        return [{key: "profile", title: t("profile", {defaultValue: "Profile"})}];
    }

    if (area === "About") {
        return [{key: "about", title: t("about-app-title", {defaultValue: "About the app"})}];
    }

    if (area === "GeneralSettings") {
        return [{key: "settings", title: t("global-settings", {defaultValue: "Global settings"})}];
    }

    return [];
};
